import { hasRole, type ChannelHealth } from '@rp/contracts'
import { useRouterState } from '@tanstack/react-router'
import { StatusBar } from '../../design'
import { useReadySession } from '../../session/useSession'
import { useChannelHealth } from './channelQueries'
import { SETTINGS_COPY } from './copy'

const SETTINGS_PATH = '/settings'

export type HealthAudience = keyof typeof SETTINGS_COPY.healthBannerAction

export interface ChannelHealthNoticeProps {
  channels: readonly ChannelHealth[] | null | undefined
  audience: HealthAudience
}

type HealthTone = 'danger' | 'warning'

const namesOf = (channels: readonly ChannelHealth[]): string[] => channels.map((channel) => channel.name)

const disconnectedOf = (channels: readonly ChannelHealth[]): ChannelHealth[] =>
  channels.filter((channel) => channel.status === 'disconnected')

const unsubscribedOf = (channels: readonly ChannelHealth[]): ChannelHealth[] =>
  channels.filter((channel) => channel.status !== 'disconnected' && channel.webhookSubscribed === false)

const problemsOf = (channels: readonly ChannelHealth[]): string[] => {
  const disconnected = disconnectedOf(channels)
  const unsubscribed = unsubscribedOf(channels)
  return [
    ...(disconnected.length === 0 ? [] : [SETTINGS_COPY.healthBannerDisconnected(namesOf(disconnected))]),
    ...(unsubscribed.length === 0 ? [] : [SETTINGS_COPY.healthBannerUnsubscribed(namesOf(unsubscribed))]),
  ]
}

const toneOf = (channels: readonly ChannelHealth[]): HealthTone =>
  disconnectedOf(channels).length === 0 ? 'warning' : 'danger'

export const ChannelHealthNotice = ({ channels, audience }: ChannelHealthNoticeProps) => {
  const pages = channels ?? []
  const problems = problemsOf(pages)
  if (problems.length === 0) return null
  const action = SETTINGS_COPY.healthBannerAction[audience]
  return (
    <div className="status-bar">
      <StatusBar tone={toneOf(pages)} title={SETTINGS_COPY.healthBannerTitle} detail={`${problems.join('')}${action}`} />
    </div>
  )
}

export const ChannelHealthBanner = () => {
  const { membership } = useReadySession()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const channels = useChannelHealth().data
  const admin = hasRole(membership.role, 'admin')
  if (admin && pathname === SETTINGS_PATH) return null
  return <ChannelHealthNotice channels={channels} audience={admin ? 'admin' : 'member'} />
}
